import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { hubTitle } from "./hubs";

// The one tab strip of the application, ported from Summit Engineering:
// - every tab is a real link with its own URL, so Back/Forward, bookmarks and deep links work;
// - WAI-ARIA tabs: role=tablist/tab, aria-selected, roving tabIndex, ArrowLeft/ArrowRight/Home/End;
// - no scroll bars: tabs keep their order, the current tab is always shown, the ones that don't fit go under "More";
// - optional live count badges with the unit in words for screen readers.

export type HubBadgeTone = "brand" | "warning" | "danger";

export interface HubTabItem {
  id: string;
  /** May carry a query string; only the path decides which tab is current. */
  to: string;
  label: string;
  icon?: LucideIcon;
  /** Only the exact path is this tab (not its sub-paths). */
  end?: boolean;
  /** Live count from an endpoint the shell already calls, shown when above zero. */
  badgeCount?: number | null;
  /** What the count counts, read by screen readers, e.g. "unread notifications". */
  badgeLabel?: string;
  badgeTone?: HubBadgeTone;
  /** Shown after the label, e.g. a data-health hint. */
  hint?: ReactNode;
  /** Tabs that belong together share a group: a thin divider separates groups, and "More" shows the group names. */
  group?: string;
}

/** Does this tab start a new group (a different group from the tab before it)? */
export function startsGroup(tabs: readonly Pick<HubTabItem, "group">[], index: number): boolean {
  return index > 0 && !!tabs[index]?.group && tabs[index]!.group !== tabs[index - 1]?.group;
}

const GROUP_DIVIDER = "mx-1.5 h-4 w-px flex-none bg-slate-200";

const BADGE_TONE: Record<HubBadgeTone, string> = {
  brand: "border-brand-300 bg-brand-50 text-brand-700",
  warning: "border-amber-300 bg-amber-50 text-amber-900",
  danger: "border-red-300 bg-red-50 text-red-800"
};

const TAB_GAP_PX = 4;

const pathOf = (to: string) => to.split(/[?#]/)[0] ?? to;

/** The tab whose path claims the current path most specifically (longest prefix; `end` tabs match exactly), or -1. */
export function activeTabIndex(tabs: readonly Pick<HubTabItem, "to" | "end">[], pathname: string): number {
  let best = -1;
  let length = -1;
  tabs.forEach((t, i) => {
    const base = pathOf(t.to);
    const hit = pathname === base || (!t.end && base !== "/" && pathname.startsWith(`${base}/`));
    if (hit && base.length > length) {
      best = i;
      length = base.length;
    }
  });
  return best;
}

/**
 * Priority+ layout: which tab indices stay in the strip so that nothing scrolls. Tabs keep their order, the current
 * tab is always shown, and the ones that don't fit go into the "More" menu (whose width is reserved once anything
 * overflows).
 */
export function computeShownTabIndices(
  tabWidths: readonly number[],
  availableWidth: number,
  moreButtonWidth: number,
  activeIndex: number,
  gap: number = TAB_GAP_PX
): number[] {
  const all = tabWidths.map((_, i) => i);
  if (tabWidths.length === 0 || availableWidth <= 0) return all;
  const total = tabWidths.reduce((sum, w) => sum + w, 0) + gap * Math.max(0, tabWidths.length - 1);
  if (total <= availableWidth) return all;

  const budget = availableWidth - moreButtonWidth - gap;
  const active = activeIndex >= 0 && activeIndex < tabWidths.length ? activeIndex : -1;
  let used = active >= 0 ? tabWidths[active]! + gap : 0;
  const shown = new Set<number>(active >= 0 ? [active] : []);
  for (let i = 0; i < tabWidths.length; i++) {
    if (i === active) continue;
    const next = tabWidths[i]! + gap;
    if (used + next > budget) break;
    used += next;
    shown.add(i);
  }
  return [...shown].sort((a, b) => a - b);
}

export interface HubTabsProps {
  hubId: string;
  /** Accessible name of the strip, e.g. "Operations sections". */
  label: string;
  /** The hub's name, used in document.title. */
  hubTitle?: string;
  tabs: readonly HubTabItem[];
  /** The current tab when the caller already knows it; otherwise matched by path. */
  activeId?: string | null;
  /** Keep document.title on the current tab (the hub strip does; second-level strips don't). */
  syncDocumentTitle?: boolean;
  /** "primary": the hub strip under the header (with icons); "secondary": a screen's own sections. */
  variant?: "primary" | "secondary";
  /** When given, the content is wrapped in the tabpanel the current tab controls. */
  children?: ReactNode;
}

export function HubTabs({ hubId, label, hubTitle: hubName, tabs, activeId, syncDocumentTitle = false, variant = "secondary", children }: HubTabsProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [shownIndices, setShownIndices] = useState<number[] | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const activeIndex = activeId !== undefined ? tabs.findIndex((t) => t.id === activeId) : activeTabIndex(tabs, pathname);
  const activeTab = activeIndex >= 0 ? tabs[activeIndex]! : null;
  const signature = tabs.map((t) => `${t.id}:${t.label}:${t.badgeCount ?? ""}`).join("|");

  useEffect(() => {
    if (!syncDocumentTitle || typeof document === "undefined") return;
    document.title = hubTitle(activeTab?.label ?? null, hubName ?? null);
  }, [activeTab?.label, hubName, syncDocumentTitle]);

  const recompute = useCallback(() => {
    const wrapper = wrapperRef.current;
    const measure = measureRef.current;
    if (!wrapper || !measure) return;
    const items = Array.from(measure.children) as HTMLElement[];
    const moreSample = items.pop();
    const widths = items.map((el) => el.offsetWidth);
    const next = computeShownTabIndices(widths, wrapper.clientWidth, moreSample?.offsetWidth ?? 0, activeIndex);
    setShownIndices((prev) => (prev && prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
  }, [activeIndex]);

  useLayoutEffect(() => {
    recompute();
    if (typeof ResizeObserver === "undefined" || !wrapperRef.current) return;
    const observer = new ResizeObserver(() => recompute());
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [recompute, signature]);

  useEffect(() => setMenuOpen(false), [pathname]);

  // Arrowing onto a tab that was under "More" opens it; once the strip shows it, it takes the focus.
  const focusAfterNavigation = useRef<string | null>(null);
  useEffect(() => {
    const id = focusAfterNavigation.current;
    if (!id) return;
    const index = tabs.findIndex((t) => t.id === id);
    const el = index >= 0 ? tabRefs.current[index] : null;
    if (el) {
      focusAfterNavigation.current = null;
      el.focus();
    }
  });

  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (moreMenuRef.current?.contains(target) || moreButtonRef.current?.contains(target)) return;
      setMenuOpen(false);
    }
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      moreButtonRef.current?.focus();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  if (tabs.length === 0) return children !== undefined ? <>{children}</> : null;

  const shown = new Set(shownIndices ?? tabs.map((_, i) => i));
  const overflow = tabs.map((tab, idx) => ({ tab, idx })).filter(({ idx }) => !shown.has(idx));
  // Roving tabindex: the current tab, or the first shown one when no tab is current.
  const focusIndex = activeIndex >= 0 ? activeIndex : tabs.findIndex((_, i) => shown.has(i));
  const tabDomId = (tab: HubTabItem) => `hub-tab-${hubId}-${tab.id}`;
  const panelId = children !== undefined ? `hub-panel-${hubId}` : undefined;

  function go(nextIndex: number) {
    const bounded = ((nextIndex % tabs.length) + tabs.length) % tabs.length;
    const target = tabs[bounded];
    if (!target) return;
    if (!shown.has(bounded)) {
      // A tab under "More": open it; the strip then shows it and it takes the focus.
      focusAfterNavigation.current = target.id;
      navigate(target.to);
      return;
    }
    tabRefs.current[bounded]?.focus();
    if (bounded !== activeIndex) navigate(target.to);
  }

  function onTabKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const focused = tabRefs.current.findIndex((el) => el === document.activeElement);
    const current = focused >= 0 ? focused : Math.max(0, activeIndex);
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(current + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(current - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      go(0);
    } else if (e.key === "End") {
      e.preventDefault();
      go(tabs.length - 1);
    }
  }

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const items = Array.from(moreMenuRef.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? []);
    const at = items.findIndex((el) => el === document.activeElement);
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(false);
      moreButtonRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(at + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(at - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Tab") {
      setMenuOpen(false);
    }
  }

  const withIcons = variant === "primary";

  function content(tab: HubTabItem, forReaders: boolean) {
    const Icon = withIcons ? tab.icon : undefined;
    const count = typeof tab.badgeCount === "number" && tab.badgeCount > 0 ? tab.badgeCount : null;
    return (
      <>
        {Icon && <Icon aria-hidden="true" className="h-3.5 w-3.5 flex-none" />}
        <span>{tab.label}</span>
        {count !== null && (
          <span
            className={`inline-flex h-4 min-w-[1.15rem] items-center justify-center rounded-full border px-1.5 text-[10px] font-semibold leading-none tabular-nums ${BADGE_TONE[tab.badgeTone ?? "brand"]}`}
            data-testid={forReaders ? `hub-tab-badge-${tab.id}` : undefined}
          >
            <span aria-hidden={forReaders ? "true" : undefined}>{count}</span>
            {forReaders && <span className="sr-only">{` (${count} ${tab.badgeLabel ?? "waiting"})`}</span>}
          </span>
        )}
        {tab.hint && <span className="text-xs text-slate-500">{tab.hint}</span>}
      </>
    );
  }

  const tabBase =
    "inline-flex flex-none items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500";
  const idle = "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900";
  const overflowHasBadge = overflow.some(({ tab }) => typeof tab.badgeCount === "number" && tab.badgeCount > 0);

  return (
    <>
      <nav aria-label={label} className="mb-4 print:hidden" data-hub-variant={variant}>
        <div ref={wrapperRef} className="relative w-full max-w-full border-b border-slate-200" data-testid={`hub-tabs-${hubId}`} data-hub-id={hubId}>
          {/* Off-screen copy: each tab's natural width, measured to decide what fits (zero-width box, so it never widens the page). */}
          <div ref={measureRef} aria-hidden="true" className="pointer-events-none invisible absolute left-0 top-0 flex h-0 w-0 flex-nowrap gap-1 overflow-hidden">
            {tabs.map((tab, idx) => (
              <span key={tab.id} className="flex flex-none items-center">
                {startsGroup(tabs, idx) && <span className={GROUP_DIVIDER} />}
                <span className={`${tabBase} border-transparent font-medium`}>{content(tab, false)}</span>
              </span>
            ))}
            <span className={`${tabBase} border-transparent`}>
              More
              <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
            </span>
          </div>

          <div className="-mb-px flex flex-nowrap items-center gap-1">
            <div role="tablist" aria-label={label} className="flex min-w-0 flex-nowrap items-center gap-1 overflow-hidden" onKeyDown={onTabKeyDown}>
              {tabs.map((tab, idx) => {
                if (!shown.has(idx)) {
                  tabRefs.current[idx] = null;
                  return null;
                }
                const selected = idx === activeIndex;
                const link = (
                  <Link
                    key={tab.id}
                    ref={(el) => {
                      tabRefs.current[idx] = el;
                    }}
                    id={tabDomId(tab)}
                    to={tab.to}
                    role="tab"
                    aria-selected={selected}
                    aria-controls={selected && panelId ? panelId : undefined}
                    tabIndex={idx === focusIndex ? 0 : -1}
                    data-testid={`hub-tab-${tab.id}`}
                    data-hub-tab-to={pathOf(tab.to)}
                    className={`${tabBase} ${selected ? "border-brand-600 font-medium text-brand-700" : idle}`}
                  >
                    {content(tab, true)}
                  </Link>
                );
                if (!startsGroup(tabs, idx)) return link;
                return (
                  <span key={tab.id} role="presentation" className="flex flex-none items-center">
                    <span aria-hidden="true" className={GROUP_DIVIDER} data-testid={`hub-group-divider-${tab.id}`} />
                    {link}
                  </span>
                );
              })}
            </div>

            {overflow.length > 0 && (
              <div className="relative flex-none">
                <button
                  ref={moreButtonRef}
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-controls={menuOpen ? `hub-more-menu-${hubId}` : undefined}
                  data-testid={`hub-more-${hubId}`}
                  onClick={(e) => {
                    const opening = !menuOpen;
                    setMenuOpen(opening);
                    // Opened from the keyboard (Enter/Space): focus moves into the menu, as the menu-button pattern asks.
                    if (opening && e.detail === 0)
                      requestAnimationFrame(() => moreMenuRef.current?.querySelector<HTMLAnchorElement>('[role="menuitem"]')?.focus());
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && !menuOpen) {
                      e.preventDefault();
                      setMenuOpen(true);
                      requestAnimationFrame(() => moreMenuRef.current?.querySelector<HTMLAnchorElement>('[role="menuitem"]')?.focus());
                    }
                  }}
                  className={`${tabBase} ${idle}`}
                >
                  More
                  <span className="sr-only">{` ${label}: ${overflow.length} more`}</span>
                  {overflowHasBadge && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-brand-600" />}
                  <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform motion-reduce:transition-none ${menuOpen ? "rotate-180" : ""}`} />
                </button>
                {menuOpen && (
                  <div
                    ref={moreMenuRef}
                    id={`hub-more-menu-${hubId}`}
                    role="menu"
                    aria-label={`More ${label}`}
                    onKeyDown={onMenuKeyDown}
                    className="absolute right-0 top-full z-40 mt-1 min-w-[14rem] max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
                  >
                    {overflow.map(({ tab }, i) => (
                      <div key={tab.id} role="none">
                        {tab.group && tab.group !== overflow[i - 1]?.tab.group && (
                          <div
                            aria-hidden="true"
                            className={`px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${i > 0 ? "mt-1 border-t border-slate-100 pt-2" : "pt-1"}`}
                            data-testid={`hub-more-group-${tab.id}`}
                          >
                            {tab.group}
                          </div>
                        )}
                        <Link
                          to={tab.to}
                          role="menuitem"
                          tabIndex={-1}
                          data-testid={`hub-more-item-${tab.id}`}
                          data-hub-tab-to={pathOf(tab.to)}
                          onClick={() => setMenuOpen(false)}
                          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                        >
                          {content(tab, true)}
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>
      {children !== undefined && (
        <div id={panelId} role="tabpanel" aria-labelledby={activeTab ? tabDomId(activeTab) : undefined} className="min-w-0" data-testid={`hub-panel-${hubId}`}>
          {children}
        </div>
      )}
    </>
  );
}
